import React, { useRef, useState, useEffect } from 'react';
import { Download, RefreshCw, RotateCcw, X, Check } from 'lucide-react';
import { useSlotAssetGeneratorStore } from '../../store/slotAssetGeneratorStore';
import type { SlotAssetResult } from '../../../core/types/slot-asset-generator.types';

interface SlotResultsModalProps {
  onSaveImage: (resultId: string) => void;
  onReroll: (resultId: string) => void;
  onRegenerateAll: () => void;
}

export const SlotResultsModal: React.FC<SlotResultsModalProps> = ({
  onSaveImage,
  onReroll,
  onRegenerateAll,
}) => {
  const generation = useSlotAssetGeneratorStore((s) => s.generation);
  const closeResultsModal = useSlotAssetGeneratorStore((s) => s.closeResultsModal);
  const openLightbox = useSlotAssetGeneratorStore((s) => s.openLightbox);
  const modalRef = useRef<HTMLDivElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    if (!modalRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsNarrow(entry.contentRect.width < 800);
      }
    });
    observer.observe(modalRef.current);
    return () => observer.disconnect();
  }, []);

  const { request, results } = generation;
  if (!request) return null;

  const isSingleImage = results.length === 1;

  return (
    <div className="absolute inset-0 z-[100] bg-zinc-900 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800">
        <span className="text-sm text-zinc-300 font-medium">
          Generated Results -- {request.component.name}
        </span>
      </div>

      {/* Subtitle metadata bar */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-zinc-800/50 text-[11px] text-zinc-500 overflow-hidden">
        <span className="truncate max-w-[200px]" title={request.prompt}>
          {request.prompt}
        </span>
        <span className="text-zinc-700">|</span>
        <span>{request.model}</span>
        <span className="text-zinc-700">|</span>
        <span>{request.style}</span>
        <span className="text-zinc-700">|</span>
        <span>{request.quality}</span>
        <span className="text-zinc-700">|</span>
        <span>{request.component.width ?? '?'}x{request.component.height ?? '?'}</span>
      </div>

      {/* Image grid */}
      <div
        ref={modalRef}
        className={`flex-1 overflow-y-auto p-4 grid gap-4
          ${isSingleImage ? 'grid-cols-1 place-items-center' :
            isNarrow ? 'grid-cols-2' :
            'grid-cols-4'}`}
      >
        {results.map((result, index) => (
          <ResultCard
            key={result.id}
            result={result}
            index={index}
            onSave={() => onSaveImage(result.id)}
            onReroll={() => onReroll(result.id)}
            onClickImage={() => openLightbox(index)}
          />
        ))}
      </div>

      {/* Bottom actions */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800">
        <button
          onClick={onRegenerateAll}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-200 border border-zinc-700 rounded hover:bg-zinc-800 transition-colors"
        >
          <RotateCcw size={12} />
          Regenerate All
        </button>
        <button
          onClick={closeResultsModal}
          className="flex items-center gap-1.5 px-4 py-1.5 text-xs bg-zinc-700 hover:bg-zinc-600 text-zinc-200 rounded transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
};

// === Individual Result Card ===

interface ResultCardProps {
  result: SlotAssetResult;
  index: number;
  onSave: () => void;
  onReroll: () => void;
  onClickImage: () => void;
}

const ResultCard: React.FC<ResultCardProps> = ({
  result,
  index,
  onSave,
  onReroll,
  onClickImage,
}) => {
  const isGenerating = result.status === 'generating';
  const isSaved = result.status === 'saved';
  const isError = result.status === 'error';

  return (
    <div className="relative rounded-lg border border-zinc-700 bg-zinc-800/50 overflow-hidden flex flex-col">
      {/* Image area */}
      <div
        className="relative aspect-square cursor-pointer bg-zinc-900 flex items-center justify-center"
        onClick={!isGenerating ? onClickImage : undefined}
        style={{
          backgroundImage: 'linear-gradient(45deg, #27272a 25%, transparent 25%), linear-gradient(-45deg, #27272a 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #27272a 75%), linear-gradient(-45deg, transparent 75%, #27272a 75%)',
          backgroundSize: '16px 16px',
          backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
        }}
      >
        {isGenerating && (
          <div className="absolute inset-0 bg-zinc-900/80 flex items-center justify-center">
            <RefreshCw size={24} className="text-blue-400 animate-spin" />
          </div>
        )}
        {isError && (
          <div className="text-xs text-red-400 text-center p-2">
            <X size={20} className="mx-auto mb-1" />
            {result.error || 'Failed'}
          </div>
        )}
        {result.imageUrl && (
          <img
            src={result.imageUrl}
            alt={`Result ${index + 1}`}
            className="max-w-full max-h-full object-contain m-auto"
          />
        )}
        {/* Saved badge */}
        {isSaved && (
          <div className="absolute top-2 right-2 bg-green-600 text-white text-[10px] font-semibold px-2 py-0.5 rounded flex items-center gap-1">
            <Check size={10} />
            SAVED
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-1.5 p-2">
        <button
          onClick={onSave}
          disabled={isGenerating || isError}
          className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded text-xs font-medium transition-colors
            ${isSaved
              ? 'bg-green-600/20 text-green-400 border border-green-600/30'
              : 'bg-green-600 hover:bg-green-500 text-white disabled:bg-zinc-700 disabled:text-zinc-500'
            }`}
        >
          {isSaved ? (
            <><Check size={12} /> Saved</>
          ) : (
            <><Download size={12} /> Use This</>
          )}
        </button>
        <button
          onClick={onReroll}
          disabled={isGenerating}
          className="flex items-center justify-center gap-1 px-3 py-1.5 rounded text-xs bg-blue-600 hover:bg-blue-500 text-white disabled:bg-zinc-700 disabled:text-zinc-500 transition-colors"
        >
          <RefreshCw size={12} />
        </button>
      </div>
    </div>
  );
};
